import React from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import * as service from '../../../services/bauordnungService';
import { EntwurfInput } from './EntwurfInput';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { sortiereRollen } from '../bauordnungLogic';
import type { BauordnungBundle, Rolle } from '../types';

/**
 * Projektorganisation mit dem UMFANG der Weisungsbefugnis.
 *
 * Der Vorgabewert ist „keine". Wer einer Rolle eine Weisungsbefugnis
 * zuschreiben will, muss das ausdruecklich tun — und bei der begrenzten
 * Befugnis nach § 6 DGUV Vorschrift 1 auch beschreiben, WIE weit sie reicht:
 * eine unbegrenzt formulierte Weisungsbefugnis gegenueber fremden
 * Beschaeftigten ist das AUeG-Risiko, und die Freigabe haelt genau danach an.
 */

interface Props {
    bundle: BauordnungBundle;
    ctx: SchreibKontext;
    gesperrt: boolean;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
}

const eingabe = 'w-full text-[13px] border border-slate-200 rounded-lg px-2.5 py-1.5 focus:border-fuchsia-400 focus:outline-none disabled:bg-slate-50';

export const RollenReiter: React.FC<Props> = ({ bundle, ctx, gesperrt, schreiben }) => {
    const o = bundle.ordnung;
    if (!o) return null;
    const rollen = sortiereRollen(bundle.rollen);

    const setze = (r: Rolle, patch: Partial<Rolle>) =>
        schreiben(() => service.updateRolle(r.id, patch, ctx));

    return (
        <div className="space-y-4 max-w-4xl">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-bold text-slate-800">Projektorganisation</h3>
                    <p className="text-[12px] text-slate-500">
                        Namen stehen als Klartext, denn die Beteiligten sind überwiegend fremde Firmen.
                    </p>
                </div>
                {!gesperrt && (
                    <button
                        onClick={() => schreiben(() => service.addRolle(o, { sortierung: rollen.length }, ctx))}
                        className="text-[12px] font-bold text-fuchsia-600 hover:text-fuchsia-700 flex items-center gap-1"
                    >
                        <Plus className="w-3.5 h-3.5" /> Rolle hinzufügen
                    </button>
                )}
            </div>

            {!rollen.length && (
                <p className="text-[12px] text-slate-400 italic border border-dashed border-slate-200 rounded-lg px-3 py-5 text-center">
                    Noch keine Rolle erfasst. Das Organigramm mit Weisungsbefugnissen ist Pflichtinhalt.
                </p>
            )}

            {rollen.map(r => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <EntwurfInput className={eingabe} placeholder="Bezeichnung im Projekt" wert={r.rolle}
                            disabled={gesperrt} aufSpeichern={v => setze(r, { rolle: v })} />
                        <EntwurfInput className={eingabe} placeholder="Name" wert={r.name}
                            disabled={gesperrt} aufSpeichern={v => setze(r, { name: v })} />
                        <EntwurfInput className={eingabe} placeholder="Firma" wert={r.firma}
                            disabled={gesperrt} aufSpeichern={v => setze(r, { firma: v })} />
                        <EntwurfInput className={eingabe} placeholder="Erreichbarkeit" wert={r.erreichbarkeit}
                            disabled={gesperrt} aufSpeichern={v => setze(r, { erreichbarkeit: v })} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr_auto] gap-2 items-start">
                        <select className={eingabe} value={r.weisungsbefugnis} disabled={gesperrt}
                            onChange={e => setze(r, { weisungsbefugnis: e.target.value as Rolle['weisungsbefugnis'] })}
                        >
                            <option value="keine">keine Weisungsbefugnis</option>
                            <option value="begrenzt_dguv1_6">begrenzt (§ 6 DGUV Vorschrift 1)</option>
                            <option value="bauleitung_lbo">Bauleitung nach LBO</option>
                            <option value="arbeitgeber">Arbeitgeber (eigene Beschäftigte)</option>
                        </select>
                        <EntwurfInput
                            className={eingabe}
                            placeholder={r.weisungsbefugnis === 'begrenzt_dguv1_6'
                                ? 'Wie weit reicht die Befugnis? (Pflichtangabe für die Freigabe)'
                                : 'Umfang der Befugnis (optional)'}
                            wert={r.befugnis_umfang} disabled={gesperrt}
                            aufSpeichern={v => setze(r, { befugnis_umfang: v })}
                        />
                        {!gesperrt && (
                            <button onClick={() => schreiben(() => service.deleteRolle(r.id, ctx))}
                                className="text-slate-300 hover:text-rose-500 p-1.5" title="Rolle löschen">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    {r.weisungsbefugnis === 'begrenzt_dguv1_6' && !r.befugnis_umfang.trim() && (
                        <p className="text-[11px] text-amber-600 flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            Ohne beschriebene Begrenzung ist die Freigabe gesperrt: eine unbegrenzte
                            Weisungsbefugnis gegenüber fremden Beschäftigten begründet ein fingiertes
                            Arbeitsverhältnis.
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};
